import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { BankAccountFormData, BankAccountType, BankAccountStatus } from '../../types/bank-account.types';
import { bankAccountService } from '../../services/bank-account.service';
export function BankAccountForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const isEditMode = !!id;
  const [formData, setFormData] = useState<BankAccountFormData>({
    bankName: '',
    branch: '',
    type: BankAccountType.CURRENT,
    accountNo: '',
    address: '',
    alias: '',
    status: BankAccountStatus.ACTIVE
  });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (isEditMode && id) {
      loadBankAccount(id);
    }
  }, [id, isEditMode]);
  const loadBankAccount = async (accountId: string) => {
    try {
      const account = await bankAccountService.getBankAccountById(accountId);
      setFormData({
        bankName: account.bankName,
        branch: account.branch,
        type: account.type,
        accountNo: account.accountNo,
        address: account.address,
        alias: account.alias,
        status: account.status
      });
    } catch (error: any) {
      alert(error.message);
    }
  };
  const handleChange = (field: keyof BankAccountFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.bankName.trim()) {
      newErrors.bankName = 'Bank name is required';
    }
    if (!formData.branch.trim()) {
      newErrors.branch = 'Branch is required';
    }
    if (!formData.accountNo.trim()) {
      newErrors.accountNo = 'Account number is required';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!formData.alias.trim()) {
      newErrors.alias = 'Alias is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      if (isEditMode && id) {
        await bankAccountService.updateBankAccount(id, formData);
      } else {
        await bankAccountService.createBankAccount(formData);
      }
      navigate('/bank-accounts');
    } catch (error: any) {
      alert(error.message);
    }
  };
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelModal(true);
    } else {
      navigate('/bank-accounts');
    }
  };
  const typeOptions = [{
    value: BankAccountType.SAVINGS,
    label: 'Savings'
  }, {
    value: BankAccountType.CURRENT,
    label: 'Current'
  }, {
    value: BankAccountType.FIXED_DEPOSIT,
    label: 'Fixed Deposit'
  }, {
    value: BankAccountType.OTHER,
    label: 'Other'
  }];
  const statusOptions = [{
    value: BankAccountStatus.ACTIVE,
    label: 'Active'
  }, {
    value: BankAccountStatus.INACTIVE,
    label: 'Inactive'
  }, {
    value: BankAccountStatus.CLOSED,
    label: 'Closed'
  }];
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-316">
      <div className="max-w-4xl mx-auto space-y-6" data-id="element-317">
        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-318">
          <Button variant="ghost" onClick={handleCancel} data-id="element-319">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-320" />
          </Button>
          <div data-id="element-321">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-322">
              {isEditMode ? 'Edit Bank Account' : 'Add Bank Account'}
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-323">
              {isEditMode ? 'Update bank account information' : 'Create a new bank account'}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border p-6 space-y-6" data-id="element-324">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-325">
            <Input label="Bank Name" required value={formData.bankName} onChange={e => handleChange('bankName', e.target.value)} error={errors.bankName} placeholder="Enter bank name" data-id="element-326" />

            <Input label="Branch" required value={formData.branch} onChange={e => handleChange('branch', e.target.value)} error={errors.branch} placeholder="Enter branch name" data-id="element-327" />

            <Select label="Type" required options={typeOptions} value={formData.type} onChange={e => handleChange('type', e.target.value)} data-id="element-328" />

            <Input label="Account No" required value={formData.accountNo} onChange={e => handleChange('accountNo', e.target.value)} error={errors.accountNo} placeholder="Enter account number" data-id="element-329" />

            <Input label="Alias" required value={formData.alias} onChange={e => handleChange('alias', e.target.value)} error={errors.alias} placeholder="Enter alias name" helperText="Short name for quick reference" data-id="element-330" />

            <Select label="Status" required options={statusOptions} value={formData.status} onChange={e => handleChange('status', e.target.value)} data-id="element-331" />

            <div className="md:col-span-2" data-id="element-332">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-333">
                Address <span className="text-red-500" data-id="element-334">*</span>
              </label>
              <textarea required className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} value={formData.address} onChange={e => handleChange('address', e.target.value)} placeholder="Enter bank branch address" data-id="element-335" />
              {errors.address && <p className="text-xs text-red-600 mt-1" data-id="element-336">{errors.address}</p>}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t" data-id="element-337">
            <Button type="button" variant="secondary" onClick={handleCancel} data-id="element-338">
              Cancel
            </Button>
            <Button type="submit" variant="primary" data-id="element-339">
              <SaveIcon className="w-4 h-4 mr-2" data-id="element-340" />
              {isEditMode ? 'Update Account' : 'Create Account'}
            </Button>
          </div>
        </form>
      </div>

      {/* Cancel Confirmation Modal */}
      <Modal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} title="Unsaved Changes" footer={<>
            <Button variant="secondary" onClick={() => setShowCancelModal(false)} data-id="element-342">
              Continue Editing
            </Button>
            <Button variant="danger" onClick={() => navigate('/bank-accounts')} data-id="element-343">
              Discard Changes
            </Button>
          </>} data-id="element-341">
        <p className="text-gray-700" data-id="element-344">
          You have unsaved changes. Are you sure you want to leave?
        </p>
      </Modal>
    </div>;
}